import { useState, useEffect } from "react";
import { Shield, CheckCircle, XCircle, ChevronRight, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../lib/config";
import { getAffiliate, useSettings } from "../lib/useSettings";

export function GoodDriverQuiz() {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);
  const settings = useSettings();

  useEffect(() => { axios.get(`${API}/quiz/questions`).then(r => setQuestions(r.data.questions)).catch(() => {}); }, []);

  const handleAnswer = (qId, score) => {
    const next = { ...answers, [qId]: score };
    setAnswers(next);
    if (current < questions.length - 1) {
      setTimeout(() => setCurrent(current + 1), 300);
    } else {
      const total = questions.length * 10;
      const sum = Object.values(next).reduce((a, b) => a + b, 0);
      axios.post(`${API}/quiz/submit`, { score: sum, total }).then(r => setResult(r.data)).catch(() => setResult({ percentage: Math.round((sum / total) * 100), level: "Good", message: "You're a solid driver!", discount_range: "5-15%" }));
    }
  };

  if (!questions.length) return null;

  if (result) {
    const color = result.percentage >= 85 ? "text-emerald-600" : result.percentage >= 65 ? "text-sky-600" : result.percentage >= 45 ? "text-amber-600" : "text-red-500";
    const bg = result.percentage >= 85 ? "bg-emerald-50 border-emerald-200" : result.percentage >= 65 ? "bg-sky-50 border-sky-200" : result.percentage >= 45 ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200";
    return (
      <div className={`rounded-2xl border p-8 text-center ${bg}`} data-testid="quiz-result">
        <div className={`text-5xl font-bold mb-2 ${color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{result.percentage}%</div>
        <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{result.level} Driver</h3>
        <p className="text-sm text-slate-600 mb-4">{result.message}</p>
        <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6 inline-block">
          <p className="text-xs text-slate-500 mb-1">Estimated discount potential</p>
          <p className="text-lg font-bold text-sky-700">{result.discount_range}</p>
        </div>
        <div className="space-y-3">
          <a href={getAffiliate(settings)} target="_blank" rel="noopener noreferrer" className="block bg-sky-700 text-white rounded-xl px-6 py-3 font-medium hover:bg-sky-800 transition-all text-sm" data-testid="quiz-result-cta">
            See How Much You Could Save <ArrowRight size={16} className="inline ml-1" />
          </a>
          <button onClick={() => { setResult(null); setCurrent(0); setAnswers({}); setStarted(false); }} className="text-sm text-slate-500 hover:text-slate-700" data-testid="quiz-retake">Take Quiz Again</button>
        </div>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="quiz-start">
        <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Shield size={28} className="text-sky-700" />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Are You a Good Driver?</h2>
        <p className="text-slate-500 text-sm mb-6 max-w-md mx-auto">Take our 2-minute quiz to find out your driver score and whether you qualify for good driver discounts.</p>
        <button onClick={() => setStarted(true)} className="bg-sky-700 text-white rounded-xl px-8 py-3 font-medium hover:bg-sky-800 transition-all shadow-sm text-sm" data-testid="quiz-start-button">
          Start the Quiz <ChevronRight size={16} className="inline ml-1" />
        </button>
        <p className="text-xs text-slate-400 mt-4">10 questions. No sign-up required. 100% free.</p>
      </div>
    );
  }

  const q = questions[current];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="quiz-active">
      <div className="flex items-center justify-between mb-6">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Question {current + 1} of {questions.length}</span>
        <div className="flex gap-1">{questions.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full ${i < current ? "bg-sky-600" : i === current ? "bg-sky-400" : "bg-slate-200"}`} />)}</div>
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{q.question}</h3>
      <div className="space-y-3">
        {q.options.map((opt, i) => (
          <button key={i} onClick={() => handleAnswer(q.id, opt.score)} className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${answers[q.id] === opt.score ? "bg-sky-50 border-sky-400 text-sky-800" : "bg-white border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50/50"}`} data-testid={`quiz-option-${i}`}>
            {opt.text}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MiniQuiz({ context, cityName }) {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const settings = useSettings();

  useEffect(() => { axios.get(`${API}/quiz/mini/${context}`).then(r => setQuestions(r.data.questions || [])).catch(() => {}); }, [context]);

  if (!questions.length) return null;

  const handleAnswer = (idx) => {
    const next = { ...answered, [current]: idx };
    setAnswered(next);
    if (current < questions.length - 1) {
      setTimeout(() => setCurrent(current + 1), 500);
    } else {
      setDone(true);
    }
  };

  const correct = Object.entries(answered).filter(([qi, ai]) => questions[parseInt(qi)]?.correct === ai).length;

  if (done) {
    return (
      <div className="bg-gradient-to-r from-sky-50 to-amber-50 border border-sky-200 rounded-xl p-6" data-testid="mini-quiz-result">
        <div className="text-center">
          <p className="text-2xl font-bold text-sky-700 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{correct}/{questions.length} Correct</p>
          <p className="text-sm text-slate-600 mb-4">{correct === questions.length ? "Perfect score! You know your coverage well." : correct >= 2 ? "Good knowledge! A few things to learn." : "Time to brush up on your auto insurance knowledge!"}</p>
          <a href={getAffiliate(settings)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-sky-700 text-white rounded-xl px-6 py-2.5 text-sm font-medium hover:bg-sky-800 transition-all" data-testid="mini-quiz-cta">
            Compare Rates{cityName ? ` in ${cityName}` : ""} <ArrowRight size={14} />
          </a>
        </div>
      </div>
    );
  }

  const q = questions[current];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="mini-quiz">
      <div className="flex items-center gap-2 mb-4">
        <Shield size={16} className="text-sky-700" />
        <span className="text-xs font-semibold text-sky-700 uppercase tracking-wider">Quick Knowledge Check</span>
        <span className="text-xs text-slate-400 ml-auto">{current + 1}/{questions.length}</span>
      </div>
      <p className="text-sm font-semibold text-slate-900 mb-3">{q.question}</p>
      <div className="space-y-2">
        {q.options.map((opt, i) => {
          const isAnswered = answered[current] !== undefined;
          const isCorrect = q.correct === i;
          const isSelected = answered[current] === i;
          let cls = "bg-white border-slate-200 text-slate-700 hover:border-sky-300";
          if (isAnswered && isSelected && isCorrect) cls = "bg-emerald-50 border-emerald-400 text-emerald-800";
          else if (isAnswered && isSelected && !isCorrect) cls = "bg-red-50 border-red-400 text-red-800";
          else if (isAnswered && isCorrect) cls = "bg-emerald-50 border-emerald-300 text-emerald-700";
          return (
            <button key={i} onClick={() => !isAnswered && handleAnswer(i)} disabled={isAnswered} className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs transition-all flex items-center gap-2 ${cls}`} data-testid={`mini-quiz-opt-${i}`}>
              {isAnswered && isCorrect && <CheckCircle size={14} className="text-emerald-500 shrink-0" />}
              {isAnswered && isSelected && !isCorrect && <XCircle size={14} className="text-red-500 shrink-0" />}
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
